/* ============================================================================
 * ตัวเลือกกลางของระบบเหรียญ (wallet coin)
 *
 * ใช้ร่วมกันสามที่ :
 *   หน้า "ตั้งค่า › ตั้งค่าเหรียญ"  — ค่าตั้งต้นและตัวช่วยแปลงบาท/เหรียญ
 *   แถบยอดเหรียญบนหัวหน้าหลังบ้าน (NavbarAdmin.js)
 *   ช่องเหรียญในตารางผู้ใช้        (page/admin/users.js — แท็บพาร์ตเนอร์)
 *
 * DEFAULT_WALLET_SETTING ต้องตรงกับ DEFAULT_WALLET_SETTING ใน
 * Munera-Backend/modules/admin/wallet/wallet.constant.js และ default ของ
 * migration 20260827000000-create_wallet_settings_table.js
 * ========================================================================== */

/* ค่าตั้งต้นของฟอร์ม — ตัวเลขเก็บเป็น string เพราะเป็นค่าใน input ที่เป็น
   controlled component (เหตุผลเดียวกับ DEFAULT_WATERMARK ในหน้าลายน้ำ) */
export const DEFAULT_WALLET_SETTING = {
  enabled: true,
  coin_name: "เหรียญ",
  coin_per_baht: "10",   // 1 บาท = 10 เหรียญ
  welcome_bonus: "0",    // เหรียญที่ให้ตอนเปิดกระเป๋า (0 = ไม่ให้)
  min_topup: "100",      // บาท ต่อการเติม 1 ครั้ง
  max_topup: "0",        // บาท (0 = ไม่จำกัด)
  low_balance: "0",      // เตือนเมื่อเหรียญเหลือน้อยกว่านี้ (0 = ไม่เตือน)
  max_adjust: "0",       // เพดานปรับยอดของผู้ดูแลต่อครั้ง (0 = ไม่จำกัด)

  /* บัญชีรับเงินของบริษัท — ฟอร์มเติมเหรียญของพาร์ตเนอร์ใช้บอกว่าโอนเข้าบัญชีไหน
     ว่างไว้ได้ ยังไม่ตั้งก็แค่ไม่มีบัญชีให้โชว์ */
  payment_bank: "",
  payment_account_no: "",
  payment_account_name: "",
  payment_promptpay: "",
  payment_note: "",
};

/* ช่องที่ค่า 0 มีความหมายว่า "ไม่จำกัด/ไม่เตือน" ไม่ใช่ "ศูนย์เหรียญ"
   ใช้ตัดสินว่าจะขึ้นข้อความกำกับใต้ช่องว่าปิดการจำกัดอยู่ */
export const UNLIMITED_FIELDS = ["max_topup", "low_balance", "max_adjust"];

export const isUnlimited = (key, value) => UNLIMITED_FIELDS.includes(key) && Number(value || 0) <= 0;

/* ยอดเติมตัวอย่างในตารางพรีวิว — ครอบช่วงที่ใช้จริงตั้งแต่เติมน้อยสุดถึงหลักพัน
   เป็นแค่ตัวอย่างการคำนวณ ไม่ใช่แพ็กเกจที่บังคับให้เลือก */
export const PREVIEW_AMOUNTS = [100, 500, 1000];

export const clone = (value) => JSON.parse(JSON.stringify(value));

/* เพดานของค่าที่กรอกได้ — ต้องตรงกับ MAX_RATE / MAX_COIN_VALUE ใน wallet.constant.js
   ฝั่ง backend ไม่งั้นกรอกผ่านหน้าเว็บได้แต่กดบันทึกไม่ผ่าน */
export const MAX_RATE = 100000;
export const MAX_COIN_VALUE = 9999999999;

/* ตัวเลขบนหน้าจอ : คั่นหลักพันด้วยคอมมา และตัด .00 ทิ้งเมื่อเป็นจำนวนเต็ม
   ไม่ใช้ toFixed ของโปรเจกต์เพราะตัวนั้นบังคับทศนิยมอย่างน้อย 2 ตำแหน่งเสมอ
   (size 0 ตกไปใช้ค่าตั้งต้น 2) ซึ่งอ่านยากกับจำนวนเหรียญที่ปกติเป็นจำนวนเต็ม */
export const fmtCoin = (value) => (Number(value) || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

/* เงินบาท -> เหรียญ ตามอัตราที่ตั้งไว้
   ปัดเป็นทศนิยม 2 ตำแหน่งเท่าคอลัมน์ NUMERIC(18,2) ฝั่งฐานข้อมูล ตัวเลขที่โชว์บนพรีวิว
   จะได้ตรงกับยอดที่เข้ากระเป๋าจริง ไม่ใช่ค่าที่ปัดคนละแบบกัน */
export const coinFromBaht = (baht, rate) => {
  const amount = Number(baht) || 0;
  const perBaht = Number(rate) || 0;
  return Math.round(amount * perBaht * 100) / 100;
};

/* เหรียญ -> เงินบาท (ใช้ตอนบอกว่าเหรียญที่ถืออยู่คิดเป็นเงินเท่าไร) */
export const bahtFromCoin = (coin, rate) => {
  const perBaht = Number(rate) || 0;
  if (perBaht <= 0) return 0;
  return Math.round(((Number(coin) || 0) / perBaht) * 100) / 100;
};

/* ========================================================== สถานะกระเป๋า */
/* ต้องตรงกับ WALLET_STATUSES ใน wallet.constant.js ฝั่ง backend
   frozen = ระงับการใช้เหรียญชั่วคราว ยอดยังอยู่ครบ (เดินตามการระงับบัญชีพาร์ตเนอร์)
   tone/tint ผ่านการตรวจ contrast บนพื้นขาวแล้ว ชุดเดียวกับสถานะใบสมัครพาร์ตเนอร์ */
export const WALLET_STATUSES = [
  { value: "active", label: "wallet_status_active", icon: "\uf058", tone: "#15803d", tint: "#dcfce7" },
  { value: "frozen", label: "wallet_status_frozen", icon: "\uf05e", tone: "#57534e", tint: "#e7e5e4" },
];

const UNKNOWN_STATUS = { value: "", label: "-", icon: "", tone: "#94a3b8", tint: "#e2e8f0" };

export const walletStatusMeta = (value) =>
  WALLET_STATUSES.find((item) => item.value === value) || UNKNOWN_STATUS;

/* ------------------------------------------------------ ประเภทรายการ */
/* ทิศทางไม่ได้อยู่ที่ประเภท — อ่านจาก direction ของรายการเสมอ (adjust เป็นได้ทั้งสองทาง) */
export const TXN_TYPES = [
  { value: "open", label: "wallet_txn_open", icon: "\uf555" },
  { value: "topup", label: "wallet_txn_topup", icon: "\uf067" },
  { value: "reward", label: "wallet_txn_reward", icon: "\uf005" },
  { value: "spend", label: "wallet_txn_spend", icon: "\uf07a" },
  { value: "refund", label: "wallet_txn_refund", icon: "\uf0e2" },
  { value: "adjust", label: "wallet_txn_adjust", icon: "\uf044" },
];

export const txnTypeMeta = (value) =>
  TXN_TYPES.find((item) => item.value === value) || { value: "", label: "-", icon: "" };

/* เครื่องหมายที่ปั๊มอยู่บนแผ่นเหรียญ — ใช้ตัวเดียวกันทุกที่ที่พูดถึง wallet coin
   (แถบด้านบน / คอลัมน์ในตาราง / การ์ดสรุป) ผู้ใช้จะได้จำสัญลักษณ์เดียว

   ไม่ใช้ fa-coins (กองเหรียญ) เพราะวงกลมไล่สีที่ครอบอยู่ "คือ" เหรียญอยู่แล้ว
   ใส่รูปเหรียญซ้อนลงบนเหรียญเป็นการพูดซ้ำ และเส้นบางสามวงซ้อนกันจะตีกันจนเละ
   เมื่อย่อลงมาอยู่ในวงกลม 24-32px — เพชรเป็นรูปทรงเรียบ เส้นน้อย อ่านออกทุกขนาด */
export const COIN_ICON = "\uf3a5";
export const WALLET_ICON = "\uf555";

/* ------------------------------------------------------------ ตัวเลข */

/**
 * จำนวนเหรียญที่แสดงบนหน้าจอ
 *
 * ใช้ en-US เสมอ (ไม่ใช่ locale ของผู้ใช้) เพราะตัวคั่นหลักพันแบบอื่นทำให้ตัวเลข
 * ในตารางเรียงไม่ตรงหลัก และเหรียญเป็นหน่วยของระบบ ไม่ใช่สกุลเงินท้องถิ่น
 *
 * ทศนิยมโชว์เฉพาะตอนที่มีเศษจริง — ยอดเต็มจำนวน (ซึ่งเป็นเกือบทั้งหมด) จะได้ไม่รก
 * ด้วย ".00" ต่อท้ายทุกบรรทัด
 */
export const coin = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "0";

  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  });
};

/** เครื่องหมายหน้าจำนวนในประวัติเหรียญ — เข้าเป็น + ออกเป็น − (ขีดยาว ไม่ใช่ hyphen) */
export const signedCoin = (direction, value) => `${direction === "debit" ? "−" : "+"}${coin(value)}`;
